import api_base, { BASE_URL } from "@/api/base";
import { AuthUser, useAuthStore } from "@/stores/authStore";
import axios from "axios";

export type ApprovalStatus = 'pending' | 'approved' | 'rejected';

export interface AuthResponse {
  access: string;
  refresh: string;
  user: AuthUser;
  email_verification_required?: boolean;
  approval_status?: ApprovalStatus;
}

export const authenticate = async (
  username: string,
  password: string,
  remember_me: boolean = false,
  firebase_device_id?: string,
  timezone?: string,
  locale?: string
): Promise<AuthResponse> => {
  const response = await api_base.post("/api/v1/login", { 
    username, 
    password,
    remember_me,
    firebase_device_id,
    timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale
  });
  return response.data;
};

export type WeekDay = 'mon' | 'tue' | 'wed' | 'thu' | 'fri';
export type AvailabilitySlot = 'morning' | 'afternoon';

export interface PublicSignup {
  cabinet_name: string;
  name: string;
  password: string;
  phone: string;
  email: string;
  availability_days: WeekDay[];
  availability_slot: AvailabilitySlot;
  firebase_device_id?: string | null;
  timezone?: string | null;
  locale?: string | null;
  preferred_language?: string | null;
}

export const publicSignup = async (
  payload: PublicSignup
): Promise<AuthResponse> => {
  const response = await api_base.post("/api/v1/signup", payload);
  return response.data;
};

export interface SignupStatus {
  approval_status: ApprovalStatus | null;
  cabinet_name?: string;
  rejection_reason?: string | null;
  availability_days?: WeekDay[];
  availability_slot?: AvailabilitySlot | null;
}

export const signupStatus = async (): Promise<SignupStatus> => {
  const response = await api_base.get("/api/v1/signup/status");
  return response.data;
};

export const forgotPassword = async (
  username: string,
  email: string
): Promise<void> => {
  await api_base.post("/api/v1/login/forgot-password", { username, email });
};

export const resetPasswordConfirm = async (
  uidb64: string,
  token: string,
  new_password: string,
  confirm_new_password: string
): Promise<void> => {
  await api_base.post(`/api/v1/login/reset-password-confirm/${uidb64}/${token}`, {
    new_password,
    confirm_new_password,
  });
};

export const verifyEmailConfirm = async (
  uidb64: string,
  token: string
): Promise<AuthResponse> => {
  const response = await api_base.post(`/api/v1/login/verify-email-confirm/${uidb64}/${token}`);
  return response.data;
};

export const verifyEmailResend = async (): Promise<void> => {
  await api_base.post("/api/v1/login/verify-email-resend");
};

export async function refreshAccessToken(): Promise<string> {
  const auth_state = useAuthStore.getState();

  const response = await axios
    .create({ baseURL: BASE_URL })
    .post("/api/v1/token/refresh", { refresh: auth_state.auth.refreshToken });
  const accessToken = response.data.access;

  auth_state.auth.setAccessToken(accessToken);
  return accessToken;
}

export async function logout() {
  const auth_state = useAuthStore.getState();
  try {
    await api_base
      .post("/api/v1/logout", { refresh: auth_state.auth.refreshToken });
  } catch (error) {
    console.warn("Logout API failed, but clearing local auth state", error);
  } finally {
    auth_state.auth.reset();
  }
}

export const me = async (): Promise<AuthUser> => {
  const response = await api_base.get("/api/v1/me");
  return response.data;
};